import React, { useEffect, useRef } from 'react';
import { styled } from '@compiled/react';

import { WidthObserver } from '@atlaskit/width-detector';
import { token } from '@atlaskit/tokens';

import { useExtensionsState } from '@atlassian/company-hub-common';
import { CardSizes } from '@atlassian/cards-extension';
import type { Card } from '@atlassian/cards-extension';

import { fg } from '@confluence/feature-gating';

type CardOrientationWrapperProps = {
	isFirstRowFull: boolean;
	cardWidthOnFullRow: number;
	cardSize?: CardSizes;
	setWidth?: React.Dispatch<React.SetStateAction<number>>;
	testId?: string;
	scrollToCard: boolean;
	index: number;
	requestScrollIndex: (index: number) => void;
	clearScrollToCard: () => void;
	cardId: Card['cardId'];
	extensionLocalId: string;
	children: React.ReactElement;
	getItemDimensions?: () => CardDimensions;
};

type CardWrapperProps = {
	isFirstRowFull: boolean;
	cardWidthOnFullRow: number;
	cardMinWidth: number;
	cardMaxWidth: number;
};

type CardDimensions = {
	minWidth: number;
	maxWidth: number;
	height: number;
};

type CardsDimensions = {
	[key in CardSizes]: CardDimensions;
};

/**
 * This is deprecated and will be removed when clean up cards updates
 */
export const cardsDimensions: CardsDimensions = {
	[CardSizes.SMALL]: {
		minWidth: 228,
		maxWidth: 380,
		height: 72,
	},
	[CardSizes.MEDIUM]: {
		minWidth: 240,
		maxWidth: 308,
		height: 312,
	},
	[CardSizes.LARGE]: {
		minWidth: 358,
		maxWidth: 640,
		height: 312,
	},
};

/**
 * Card dimensions for cards 1.0
 *
 * - The max number of cards per row determines the min width of different card sizes
 * - The max width is used in case the first row is not full
 */
export const cardsDimensionsV1: Pick<
	CardsDimensions,
	CardSizes.SMALL | CardSizes.MEDIUM | CardSizes.LARGE
> = {
	[CardSizes.SMALL]: {
		minWidth: 240,
		maxWidth: 294,
		height: 84,
	},
	[CardSizes.MEDIUM]: {
		minWidth: 240,
		maxWidth: 294,
		height: 320,
	},
	[CardSizes.LARGE]: {
		minWidth: 326,
		maxWidth: 392,
		height: 320,
	},
};

export const getCardDimensions = (cardSize: CardSizes, isCardsVisualUpdateEnabled: boolean) => {
	if (isCardsVisualUpdateEnabled && cardSize in cardsDimensionsV1) {
		return cardsDimensionsV1[cardSize];
	}

	return cardsDimensions[cardSize];
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- To migrate as part of go/ui-styling-standard
export const CardWrapper = styled.div<CardWrapperProps>({
	flex: 1,
	paddingBlock: token('space.025'),
	position: 'relative',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	...({ isFirstRowFull, cardWidthOnFullRow, cardMinWidth, cardMaxWidth }) => ({
		minWidth: isFirstRowFull ? `${cardWidthOnFullRow}px` : `${cardMinWidth}px`,
		maxWidth: isFirstRowFull ? `${cardWidthOnFullRow}px` : `${cardMaxWidth}px`,
	}),
});

export const CardOrientationWrapper: React.FC<CardOrientationWrapperProps> = ({
	children,
	isFirstRowFull,
	cardWidthOnFullRow,
	cardSize,
	setWidth,
	testId = 'card-wrapper',
	scrollToCard,
	clearScrollToCard,
	index,
	requestScrollIndex,
	cardId,
	extensionLocalId,
	getItemDimensions,
}) => {
	const cardRef = useRef<HTMLDivElement>(null);
	const [{ selectedExtensionLocalId, selectedToEditCardId }] = useExtensionsState();
	const cardDimensions = getItemDimensions
		? getItemDimensions()
		: getCardDimensions(cardSize || CardSizes.MEDIUM, fg('company_hub_cards_visual_update'));

	useEffect(() => {
		const scrollIntoViewBehavior: ScrollIntoViewOptions = fg('company_hub_carousel_v2')
			? { behavior: 'smooth', block: 'nearest', inline: 'nearest' }
			: { behavior: 'smooth', block: 'center' };
		if (cardRef.current) {
			// Scroll to this card as long as its config panel is open OR no config panel is open.
			// If no config panel is open, that covers the case in edit mode where user has clicked
			// scroll buttons on a row-oriented cards instance which they aren't actively editing.
			if (
				scrollToCard &&
				(!selectedExtensionLocalId || extensionLocalId === selectedExtensionLocalId)
			) {
				cardRef.current.scrollIntoView(scrollIntoViewBehavior);

				// Clear scrollIndex to prevent re-invoking scroll effect later unexpectedly
				clearScrollToCard();
			}
			// Handle the case where user has selected this card to edit from the config panel.
			if (
				selectedToEditCardId &&
				selectedToEditCardId === cardId &&
				extensionLocalId === selectedExtensionLocalId
			) {
				// Now that we've reached the right card that user is selecting to edit,
				// trigger the scrolling hook to recalculate scroll window and index so
				// that we can rerender invoking the scroll effect
				requestScrollIndex(index);
			} else if (!selectedToEditCardId) {
				// if no card is selected to edit, set index to -1 indicate that it is not currently being used
				requestScrollIndex(-1);
			}
		}
	}, [
		cardRef,
		cardId,
		extensionLocalId,
		index,
		scrollToCard,
		clearScrollToCard,
		selectedExtensionLocalId,
		selectedToEditCardId,
		requestScrollIndex,
	]);

	return (
		<CardWrapper
			ref={cardRef}
			data-testid={testId}
			isFirstRowFull={isFirstRowFull}
			cardWidthOnFullRow={cardWidthOnFullRow}
			cardMinWidth={cardDimensions.minWidth}
			cardMaxWidth={cardDimensions.maxWidth}
		>
			{children}
			{index === 0 && setWidth && <WidthObserver setWidth={setWidth} offscreen />}
		</CardWrapper>
	);
};
